// www/src/components/ServiceEditor.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import ImageInput from './ImageInput';

function slugify(s) {
  return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function emptyPricing() { return { durationMinutes: 60, price: '', label: '', sortOrder: 0 }; }

export default function ServiceEditor({ service, onDone, onCancel }) {
  const { authenticatedFetch } = useAuth();
  const [form, setForm] = useState(() => service ? {
    id: service.id,
    name: service.name, slug: service.slug,
    description: service.description || '', image: service.image || '',
    published: !!service.published, sortOrder: service.sortOrder || 0,
    pricing: service.pricing?.length ? service.pricing.map((p) => ({
      durationMinutes: p.durationMinutes, price: String(p.price), label: p.label || '', sortOrder: p.sortOrder || 0,
    })) : [emptyPricing()],
  } : {
    name: '', slug: '', description: '', image: '', published: true, sortOrder: 0,
    pricing: [emptyPricing()],
  });
  const [saving, setSaving] = useState(false);

  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  function updateRow(kind, i, patch) { setForm((f) => ({ ...f, [kind]: f[kind].map((r, idx) => idx === i ? { ...r, ...patch } : r) })); }
  function removeRow(kind, i) { setForm((f) => ({ ...f, [kind]: f[kind].filter((_, idx) => idx !== i) })); }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    const slug = form.slug || slugify(form.name);
    const body = {
      name: form.name, slug,
      description: form.description, image: form.image || null,
      published: form.published, sortOrder: Number(form.sortOrder) || 0,
      pricing: form.pricing
        .filter((p) => p.price !== '' && p.durationMinutes)
        .map((p) => ({
          durationMinutes: Number(p.durationMinutes),
          price: Number(p.price),
          label: p.label || null,
          sortOrder: Number(p.sortOrder) || 0,
        })),
    };
    const url = form.id ? `${API_BASE_URL}/api/services/${form.id}` : `${API_BASE_URL}/api/services`;
    const method = form.id ? 'PUT' : 'POST';
    const res = await authenticatedFetch(url, {
      method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    setSaving(false);
    if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.error || 'Save failed'); return; }
    onDone();
  }

  return (
    <form className="service-editor" onSubmit={onSave}>
      <h2>{form.id ? 'Edit Service' : 'New Service'}</h2>
      <label>Name <input value={form.name} onChange={(e) => update('name', e.target.value)} required /></label>
      <label>Slug <input value={form.slug} onChange={(e) => update('slug', e.target.value)} placeholder="auto from name" /></label>
      <label>Description <textarea rows={4} value={form.description} onChange={(e) => update('description', e.target.value)} /></label>
      <ImageInput label="Image" value={form.image} onChange={(v) => update('image', v)} />
      <label><input type="checkbox" checked={form.published} onChange={(e) => update('published', e.target.checked)} /> Published</label>
      <label>Sort order <input type="number" value={form.sortOrder} onChange={(e) => update('sortOrder', e.target.value)} /></label>

      <fieldset>
        <legend>Pricing</legend>
        <p className="editor-help">One row per duration/price option for this service. Leave label blank to display as "60 min" on the public site.</p>
        {form.pricing.length > 0 && (
          <div className="editor-row-head">
            <span>Duration (min)</span>
            <span>Price ($)</span>
            <span>Label (optional)</span>
            <span className="head-actions"></span>
          </div>
        )}
        {form.pricing.map((p, i) => (
          <div key={i} className="editor-row">
            <input type="number" placeholder="60" value={p.durationMinutes} onChange={(e) => updateRow('pricing', i, { durationMinutes: e.target.value })} />
            <input type="number" step="0.01" placeholder="85.00" value={p.price} onChange={(e) => updateRow('pricing', i, { price: e.target.value })} />
            <input placeholder="e.g. 90 min Signature" value={p.label} onChange={(e) => updateRow('pricing', i, { label: e.target.value })} />
            <button type="button" className="btn-icon" title="Remove pricing row" onClick={() => removeRow('pricing', i)}>×</button>
          </div>
        ))}
        <button type="button" className="btn-secondary" onClick={() => setForm((f) => ({ ...f, pricing: [...f.pricing, emptyPricing()] }))}>+ Pricing row</button>
      </fieldset>

      <div className="editor-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
